import React, { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Grid, Tooltip } from '@material-ui/core';
import statesInverted from '@/constants/statesInverted.json';
import { routeName } from '@/utilities/routeNames';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import DoneIcon from '@mui/icons-material/Done';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import { genDestinationAttributes, genOriginAttributes } from '@onward-delivery/core';
import { calcAccessorialsPerspective, calcSubtotalPerspective, formatOrderIdentifier } from '../Invoice/utils';
import { STAGES } from './constants';
import { fmMargin, fmPayableRate, isCarrierRateSuppressed, mostRecentRoute } from './utils';
import { StatusDisplay } from '@/components/ShipperMyRoutes/tableColumns/orderTableBase';
const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
});

export const integerFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const RouteRowLabel = ({ children }) => (
    <Box display="flex" alignItems="center">
        <span
            css={css`
                font-weight: 700;
                color: ${colors.greys[3]};
            `}
        >
            {children}
        </span>
    </Box>
);

const ACCESSORIALS_PERSPECTIVE = [
    { attr: 'shipper_rate_override_id', perspective: 'shipper', label: 'Shipper' },
    { attr: 'middle_mile_shipper_rate_override_id', perspective: 'mm_shipper', label: 'MM Shipper' },
    { attr: 'warehouse_shipper_rate_override_id', perspective: 'wh_shipper', label: 'WH Shipper' },
    { attr: 'carrier_rate_override_id', perspective: 'carrier', label: 'Carrier' },
    { attr: 'middle_mile_carrier_rate_override_id', perspective: 'mm_carrier', label: 'MM Carrier' },
    { attr: 'warehouse_carrier_rate_override_id', perspective: 'wh_carrier', label: 'WH Carrier' },
];

export const useColumns = ({ state, callbacks, filter }) => {
    return useMemo(() => {
        return [
            {
                Header: 'Order Details',
                id: 'order-details-group',
                columns: [
                    ...([STAGES.INVOICED, STAGES.APPROVED, STAGES.PAID].includes(filter.stage)
                        ? [
                              {
                                  Header: 'Invoice',
                                  id: 'invoice',
                                  width: 100,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      return order.shipper_invoice?.invoice_number || '--';
                                  },
                              },
                          ]
                        : []),
                    {
                        Header: 'Order',
                        id: 'order_number',
                        width: 200,
                        accessor: (order) => (order.isRouteRow ? '' : formatOrderIdentifier(order)),
                        Cell: ({ value, row }) => {
                            if (row.original.isRouteRow) {
                                return (
                                    <span
                                        css={css`
                                            font-weight: 700;
                                            color: ${colors.greys[3]};
                                        `}
                                    >
                                        Route
                                    </span>
                                );
                            }
                            return value;
                        },
                    },
                    ...(state.auditEnabled && filter.stage === STAGES.PENDING_APPROVAL
                        ? [
                              {
                                  Header: 'Reviewed',
                                  id: 'invoicing_status',
                                  width: 110,
                                  disableSortBy: true,
                                  accessor: (order) => (order.isRouteRow ? '' : order.invoicing_status === 'PRE_APPROVED'),
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      return order.invoicing_status === 'PRE_APPROVED' ? (
                                          <Tooltip arrow placement="top" title="Reviewed in accessorial audit">
                                              <CheckCircleIcon
                                                  css={css`
                                                      color: ${colors.greens.primary};
                                                  `}
                                              />
                                          </Tooltip>
                                      ) : (
                                          '--'
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(state.routeRatesEnabled
                        ? [
                              {
                                  Header: 'Route #',
                                  id: 'route',
                                  width: 120,
                                  disableSortBy: true,
                                  accessor: (order) => {
                                      if (order.isRouteRow) {
                                          return order.route ? order.route.route_number ?? '--' : 'Non Routed Orders';
                                      }
                                      return mostRecentRoute(order)?.route_number ?? '--';
                                  },
                                  Cell: ({ value, row }) =>
                                      row.original.isRouteRow ? (
                                          <RouteRowLabel>
                                              {value}
                                              {row.original.routeOrderCount ? ` (${row.original.routeOrderCount})` : ''}
                                          </RouteRowLabel>
                                      ) : (
                                          value
                                      ),
                              },
                              {
                                  Header: 'Route Name',
                                  id: 'route_name',
                                  width: 160,
                                  disableSortBy: true,
                                  accessor: (order) =>
                                      routeName(order.isRouteRow ? order.route : mostRecentRoute(order)) || '--',
                                  Cell: ({ value, row }) =>
                                      row.original.isRouteRow ? <RouteRowLabel>{value}</RouteRowLabel> : value,
                              },
                          ]
                        : []),
                    {
                        Header: 'Shipper',
                        id: 'shipper',
                        width: 200,
                        Cell: ({ row }) => {
                            const order = row.original;
                            return order.order_shipper?.business_name || '--';
                        },
                    },
                    {
                        Header: 'Carrier',
                        id: 'carrier',
                        width: 200,
                        Cell: ({ row }) => {
                            const order = row.original;
                            return order.order_carrier?.business_name || '--';
                        },
                    },
                    {
                        Header: 'Contractor',
                        id: 'contractor',
                        width: 200,
                        Cell: ({ row }) => {
                            const order = row.original;
                            return order.order_contractor?.business_name || '--';
                        },
                    },
                    {
                        Header: 'Completed',
                        id: 'completed_date',
                        width: 150,
                        accessor: (order) => order.billing_completion_time,
                        Cell: ({ row }) => {
                            const order = row.original;
                            if (order.isRouteRow) return '';
                            return order.billing_completion_time_formatted || '--';
                        },
                    },
                    {
                        Header: 'POD',
                        id: 'pod',
                        width: 125,
                        Cell: ({ row }) => {
                            const order = row.original;
                            if (order.isRouteRow) return '';
                            const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                            const hasSignature = order.customer_signature?.length >= 1;

                            return hasSignature || hasPhoto ? (
                                <>
                                    <DoneIcon
                                        css={css`
                                            color: ${colors.greens.primary};
                                        `}
                                    />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            callbacks.viewOrderPod(order);
                                        }}
                                        css={css`
                                            color: ${colors.greens.primary};
                                            text-decoration: underline;
                                            margin: 5px;
                                            font-weight: 500;
                                            background: none;
                                            border: none;
                                            cursor: pointer;
                                        `}
                                    >
                                        View
                                    </button>
                                </>
                            ) : (
                                <CloseIcon
                                    css={css`
                                        color: ${colors.reds.primary};
                                        background: none;
                                    `}
                                />
                            );
                        },
                    },
                    {
                        Header: 'Issues',
                        id: 'issues',
                        width: 110,
                        accessor: (order) => {
                            if (order.isRouteRow) return false;
                            return (
                                order.exceptions.filter((mapping) => mapping.exception.status !== 'RESOLVED').length > 0
                            );
                        },
                        Cell: ({ value, row }) => {
                            if (row.original.isRouteRow) return '';
                            if (value) {
                                return (
                                    <WarningIcon
                                        css={css`
                                            color: ${colors.reds.primary};
                                        `}
                                    />
                                );
                            }

                            return '--';
                        },
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        width: 200,
                        accessor: (order) => order.order_status,
                        Cell: ({ value, row }) => {
                            if (row.original.isRouteRow) return '';
                            return <StatusDisplay status={value} />;
                        },
                    },
                    {
                        Header: 'Customer',
                        id: 'dropoff_name',
                        width: 200,
                        accessor: (order) => (order.isRouteRow ? '' : order.dropoff_name),
                    },
                    {
                        Header: 'Origin',
                        id: 'origin',
                        width: 200,
                        accessor: (order) => {
                            if (order.isRouteRow) return '';
                            const {
                                city: cityAttr,
                                state: stateAttr,
                                zip: zipAttr,
                            } = genOriginAttributes(order, false, true);
                            const state = statesInverted[order[stateAttr]] || order[stateAttr];
                            return `${order[cityAttr]}, ${state} ${order[zipAttr]}`;
                        },
                    },
                    {
                        Header: 'Destination',
                        id: 'destination',
                        width: 200,
                        accessor: (order) => {
                            if (order.isRouteRow) return '';
                            const {
                                city: cityAttr,
                                state: stateAttr,
                                zip: zipAttr,
                            } = genDestinationAttributes(order, false, true);
                            const state = statesInverted[order[stateAttr]] || order[stateAttr];
                            return `${order[cityAttr]}, ${state} ${order[zipAttr]}`;
                        },
                        Cell: ({ value, row: order }) => {
                            if (order.original.isRouteRow) return '';
                            if (order.original.dropoff_address_confirmed) {
                                return (
                                    <Grid
                                        container
                                        css={css`
                                            align-items: center;
                                            flex-wrap: nowrap;
                                        `}
                                    >
                                        <Grid
                                            item
                                            css={css`
                                                margin-right: 4px;
                                            `}
                                        >
                                            <Tooltip arrow placement="top" title={`Customer confirmed`}>
                                                <CheckCircleIcon
                                                    css={css`
                                                        color: ${colors.greens.primary};
                                                    `}
                                                />
                                            </Tooltip>
                                        </Grid>
                                        {value}
                                    </Grid>
                                );
                            } else {
                                return value;
                            }
                        },
                    },
                    ...(state.isAdmin || state.isCarrierAdmin
                        ? (() => {
                              // Returns true when this user can see shipper-side figures for the given row.
                              // For carrier admins, individual T2/T3 orders and any route row containing
                              // at least one such order are suppressed.
                              const canSeeShipperFigures = (order) => {
                                  if (state.isAdmin) return true;
                                  if (order.isRouteRow) {
                                      return (order.routeOrders || []).every(
                                          (o) => !!state.editableOrders[o.order_id]
                                      );
                                  }
                                  return !!state.editableOrders[order.order_id];
                              };
                              return [
                              {
                                  Header: 'Shipper FM Subtotal',
                                  id: 'shipper-fm-subtotal',
                                  width: 175,
                                  accessor: (order) =>
                                      !canSeeShipperFigures(order) ? null : order.isRouteRow ? 0 : calcSubtotalPerspective(order, 'shipper') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const value = calcSubtotalPerspective(order, 'shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Shipper FM Accessorials',
                                  id: 'shipper-fm-accessorials',
                                  width: 175,
                                  accessor: (order) =>
                                      !canSeeShipperFigures(order) ? null : order.isRouteRow ? 0 : calcAccessorialsPerspective(order, 'shipper') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const value = calcAccessorialsPerspective(order, 'shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Shipper FM Total',
                                  id: 'shipper-fm-total',
                                  width: 175,
                                  accessor: (order) => canSeeShipperFigures(order) ? order.shipper_rate : null,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      const total = order.shipper_rate || 0;
                                      if (order.isRouteRow) {
                                          if (!canSeeShipperFigures(order)) return '--';
                                          return (
                                              <span
                                                  css={css`
                                                      font-weight: 700;
                                                  `}
                                              >
                                                  {total ? dollarFormatter.format(total) : '--'}
                                              </span>
                                          );
                                      }
                                      if (!canSeeShipperFigures(order)) return '--';
                                      return dollarFormatter.format(total);
                                  },
                              },
                              {
                                  Header: 'Carrier FM Subtotal',
                                  id: 'carrier-fm-subtotal',
                                  width: 175,
                                  accessor: (order) =>
                                      isCarrierRateSuppressed(order)
                                          ? null
                                          : order.isRouteRow
                                          ? 0
                                          : calcSubtotalPerspective(order, 'carrier') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (isCarrierRateSuppressed(order)) return 'N/A';
                                      if (order.isRouteRow) return '';
                                      const value = calcSubtotalPerspective(order, 'carrier');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Carrier FM Accessorials',
                                  id: 'carrier-fm-accessorials',
                                  width: 175,
                                  accessor: (order) =>
                                      isCarrierRateSuppressed(order)
                                          ? null
                                          : order.isRouteRow
                                          ? 0
                                          : calcAccessorialsPerspective(order, 'carrier') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (isCarrierRateSuppressed(order)) return 'N/A';
                                      if (order.isRouteRow) return '';
                                      const value = calcAccessorialsPerspective(order, 'carrier');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Carrier FM Total',
                                  id: 'carrier-fm-total',
                                  width: 175,
                                  accessor: (order) => (isCarrierRateSuppressed(order) ? null : order.carrier_rate),
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (isCarrierRateSuppressed(order)) return 'N/A';
                                      const total = order.carrier_rate || 0;
                                      if (order.isRouteRow) {
                                          return (
                                              <span
                                                  css={css`
                                                      font-weight: 700;
                                                  `}
                                              >
                                                  {total ? dollarFormatter.format(total) : '--'}
                                              </span>
                                          );
                                      }
                                      return dollarFormatter.format(total);
                                  },
                              },
                              {
                                  Header: 'Contractor FM Subtotal',
                                  id: 'contractor-fm-subtotal',
                                  width: 175,
                                  accessor: (order) =>
                                      order.isRouteRow ? 0 : calcSubtotalPerspective(order, 'contractor') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      const value = calcSubtotalPerspective(order, 'contractor');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Contractor FM Accessorials',
                                  id: 'contractor-fm-accessorials',
                                  width: 175,
                                  accessor: (order) =>
                                      order.isRouteRow ? 0 : calcAccessorialsPerspective(order, 'contractor') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      const value = calcAccessorialsPerspective(order, 'contractor');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Contractor FM Total',
                                  id: 'contractor-fm-total',
                                  width: 175,
                                  accessor: (order) => order.contractor_rate,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      const total = order.contractor_rate || 0;
                                      if (order.isRouteRow) {
                                          return (
                                              <span
                                                  css={css`
                                                      font-weight: 700;
                                                  `}
                                              >
                                                  {total ? dollarFormatter.format(total) : '--'}
                                              </span>
                                          );
                                      }
                                      return total ? dollarFormatter.format(total) : '--';
                                  },
                              },
                              {
                                  Header: 'FM Margin',
                                  id: 'fm-margin',
                                  width: 150,
                                  accessor: (order) => (canSeeShipperFigures(order) ? fmMargin(order) : null),
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const payableRate = fmPayableRate(order);
                                      const value = fmMargin(order);
                                      const pct = parseInt(!!payableRate ? (value / payableRate) * 100 : 0);
                                      return (
                                          <span
                                              css={css`
                                                  color: ${value >= 0 ? colors.greens.primary : colors.reds.primary};
                                                  font-weight: 700;
                                              `}
                                          >
                                              {dollarFormatter.format(value)} ({pct}%)
                                          </span>
                                      );
                                  },
                              },
                              {
                                  Header: 'Shipper MM Subtotal',
                                  id: 'shipper-mm-subtotal',
                                  width: 175,
                                  accessor: (order) =>
                                      !canSeeShipperFigures(order) ? null : order.isRouteRow ? 0 : calcSubtotalPerspective(order, 'mm_shipper') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const value = calcSubtotalPerspective(order, 'mm_shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Shipper MM Accessorials',
                                  id: 'shipper-mm-accessorials',
                                  width: 175,
                                  accessor: (order) =>
                                      !canSeeShipperFigures(order) ? null : order.isRouteRow ? 0 : calcAccessorialsPerspective(order, 'mm_shipper') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const value = calcAccessorialsPerspective(order, 'mm_shipper');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Shipper MM Total',
                                  id: 'shipper-mm-total',
                                  width: 175,
                                  accessor: (order) => canSeeShipperFigures(order) ? order.middle_mile_shipper_rate : null,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      const total = order.middle_mile_shipper_rate || 0;
                                      if (order.isRouteRow) {
                                          if (!canSeeShipperFigures(order)) return '--';
                                          return (
                                              <span
                                                  css={css`
                                                      font-weight: 700;
                                                  `}
                                              >
                                                  {total ? dollarFormatter.format(total) : '--'}
                                              </span>
                                          );
                                      }
                                      if (!canSeeShipperFigures(order)) return '--';
                                      return total ? dollarFormatter.format(total) : '--';
                                  },
                              },
                              {
                                  Header: 'Carrier MM Subtotal',
                                  id: 'carrier-mm-subtotal',
                                  width: 175,
                                  accessor: (order) =>
                                      order.isRouteRow ? 0 : calcSubtotalPerspective(order, 'mm_carrier') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      const value = calcSubtotalPerspective(order, 'mm_carrier');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Carrier MM Accessorials',
                                  id: 'carrier-mm-accessorials',
                                  width: 175,
                                  accessor: (order) =>
                                      order.isRouteRow ? 0 : calcAccessorialsPerspective(order, 'mm_carrier') ?? 0,
                                  sortType: 'basic',
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      const value = calcAccessorialsPerspective(order, 'mm_carrier');
                                      return value ? dollarFormatter.format(value) : '--';
                                  },
                              },
                              {
                                  Header: 'Carrier MM Total',
                                  id: 'carrier-mm-total',
                                  width: 175,
                                  accessor: (order) => order.middle_mile_carrier_rate,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      const total = order.middle_mile_carrier_rate || 0;
                                      if (order.isRouteRow) {
                                          return (
                                              <span
                                                  css={css`
                                                      font-weight: 700;
                                                  `}
                                              >
                                                  {total ? dollarFormatter.format(total) : '--'}
                                              </span>
                                          );
                                      }
                                      return total ? dollarFormatter.format(total) : '--';
                                  },
                              },
                              {
                                  Header: 'MM Margin',
                                  id: 'mm-margin',
                                  width: 150,
                                  accessor: (order) =>
                                      canSeeShipperFigures(order)
                                          ? (order.middle_mile_shipper_rate || 0) - (order.middle_mile_carrier_rate || 0)
                                          : null,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      if (!canSeeShipperFigures(order)) return '--';
                                      const shipperRate = order.middle_mile_shipper_rate || 0;
                                      const carrierRate = order.middle_mile_carrier_rate || 0;
                                      const value = shipperRate - carrierRate;
                                      const pct = parseInt(!!carrierRate ? (value / carrierRate) * 100 : 0);
                                      return (
                                          <span
                                              css={css`
                                                  color: ${value >= 0 ? colors.greens.primary : colors.reds.primary};
                                                  font-weight: 700;
                                              `}
                                          >
                                              {dollarFormatter.format(value)} ({pct}%)
                                          </span>
                                      );
                                  },
                              },
                          ]; })()
                        : [
                              {
                                  Header: 'Revenue',
                                  id: 'revenue',
                                  width: 175,
                                  accessor: (order) => (order.oms ? order.order_revenue : order.carrier_rate),
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      if (order.isRouteRow) return '';
                                      const total = (order.oms ? order.order_revenue : order.carrier_rate) || 0;
                                      return dollarFormatter.format(total);
                                  },
                              },
                          ]),
                ],
            },
            {
                sticky: 'right',
                Header: '',
                id: 'sticky-right',
                columns: [
                    {
                        id: 'show_hidden_columns',
                        Header: '',
                        accessor: () => '',
                        width: 52,
                        minWidth: 52,
                        maxWidth: 52,
                        disableSortBy: true,
                        disableResizing: true,
                        Cell: ({ row }) => {
                            const order = row.original;
                            const [anchorEl, setAnchorEl] = React.useState(null);

                            // Route header rows only get a menu when they can be audited.
                            const canAuditRoute =
                                order.isRouteRow &&
                                state.auditEnabled &&
                                state.invoicingPermissions &&
                                callbacks.openAuditModal &&
                                filter.stage === STAGES.PENDING_APPROVAL &&
                                order.routeOrders?.length > 0;

                            if (order.isRouteRow && !canAuditRoute) return null;

                            const handleClick = (event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                setAnchorEl(event.currentTarget);
                            };

                            const handleClose = (event) => {
                                event?.stopPropagation();
                                event?.preventDefault();
                                setAnchorEl(null);
                            };

                            return (
                                <div
                                    css={css`
                                        color: #59b863;
                                        font-weight: 600;
                                        display: flex-start;
                                        justify-content: flex-start;
                                        align-items: center;
                                    `}
                                >
                                    <IconButton
                                        onClick={handleClick}
                                        css={css`
                                            font-weight: 600;
                                            padding: 4px;
                                            justify-content: center;
                                        `}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Popover
                                        open={Boolean(anchorEl)}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        style={{ padding: '10px', borderRadius: '20px' }}
                                    >
                                        <Grid direction="column" container>
                                            {order.isRouteRow ? (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.openAuditModal(order);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Audit Route
                                                    </Button>
                                                </Grid>
                                            ) : (
                                                <>
                                                    <Grid direction="row" container>
                                                        <Button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                callbacks.gotoDetails(order.order_id);
                                                                handleClose();
                                                            }}
                                                            css={css`
                                                                width: 100%;
                                                                justify-content: flex-start;
                                                            `}
                                                        >
                                                            View Order
                                                        </Button>
                                                    </Grid>
                                                    {state.editableOrders[order.order_id] ? (
                                                        <Grid direction="row" container>
                                                            <Button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    callbacks.editOrder(order);
                                                                    handleClose();
                                                                }}
                                                                css={css`
                                                                    width: 100%;
                                                                    justify-content: flex-start;
                                                                `}
                                                            >
                                                                Edit Order
                                                            </Button>
                                                        </Grid>
                                                    ) : null}
                                                    {order.invoicing_status !== 'APPROVED' &&
                                                    !order.shipper_invoice &&
                                                    state.editableOrders[order.order_id] ? (
                                                        <Grid direction="row" container>
                                                            <Button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    callbacks.approveId(order.order_id);
                                                                    handleClose();
                                                                }}
                                                                css={css`
                                                                    width: 100%;
                                                                    justify-content: flex-start;
                                                                `}
                                                            >
                                                                Approve
                                                            </Button>
                                                        </Grid>
                                                    ) : null}
                                                    {order.invoicing_status === 'APPROVED' &&
                                                    !order.shipper_invoice &&
                                                    state.editableOrders[order.order_id] ? (
                                                        <Grid direction="row" container>
                                                            <Button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    callbacks.invoiceId(order.order_id);
                                                                    handleClose();
                                                                }}
                                                                css={css`
                                                                    width: 100%;
                                                                    justify-content: flex-start;
                                                                `}
                                                            >
                                                                Create Invoice
                                                            </Button>
                                                        </Grid>
                                                    ) : null}
                                                </>
                                            )}
                                        </Grid>
                                    </Popover>
                                </div>
                            );
                        },
                    },
                ],
            },
        ];
    }, [state, callbacks, filter]);
};

export const QUICKBOOKS_CSV_COLUMNS = [
    {
        header: 'Invoice No.',
        getValue: ({ order }) => order.shipper_invoice?.invoice_number || '',
    },
    {
        header: 'Shipper',
        getValue: ({ order }) => order.order_shipper?.business_name || '',
    },
    {
        header: 'Invoice Date',
        getValue: ({ order }) => {
            if (!order.shipper_invoice?.created_at) {
                return undefined;
            }
            const date = new Date(order.shipper_invoice?.created_at);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Due Date',
        getValue: ({ order }) => {
            if (!order.shipper_invoice?.due_date) {
                return undefined;
            }
            const date = new Date(order.shipper_invoice?.due_date);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Date Completed',
        getValue: ({ order }) => {
            if (!order.billing_completion_time) {
                return undefined;
            }
            const date = new Date(order.billing_completion_time);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Tracking No.',
        getValue: ({ order }) => order.order_number,
    },
    {
        header: 'Purchase Order',
        getValue: ({ order }) => [order.po_number, order.reference_id].filter(Boolean).join(' / '),
    },
    {
        header: 'Product/Service',
        getValue: ({ row }) => {
            return row?.qbo_tag || '';
        },
    },
    {
        header: 'Description',
        getValue: ({ row }) => {
            return row?.description || '';
        },
    },
    {
        header: 'Qty',
        getValue: ({ row }) => {
            return row?.quantity || '';
        },
    },
    {
        header: 'Rate',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.rate || 0);
        },
    },
    {
        header: 'Amount',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.total || 0);
        },
    },
    {
        header: 'Class',
        getValue: ({ order }) => {
            return order?.invoice_class || '';
        },
    },
    {
        header: 'Memo',
        getValue: ({ order }) => {
            return [order.order_number, order.po_number, order.reference_id].filter(Boolean).join(' / ');
        },
    },
];
